import { apiClient } from '../api';
import { apiClientBff } from '../apiBff';
import { API_CONFIG } from '../../config/api';

// Types for Farm - Updated to match database schema
export interface Farm {
  // ArangoDB fields
  _key?: string;
  _id?: string;
  _rev?: string;
  
  // Legacy field for compatibility
  id?: string;
  
  // Farm data fields
  owner_id: string;
  name: string;
  registration_number?: string;
  farm_type: string[] | string; // Can be array or single value
  production_focus: string;
  country_code: string;
  location: {
    address: string;
    region: string;
    postal_code: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  area_hectares: number;
  capacity_head: number;
  facilities: string[]; // Array of facility names
  certifications: string[];
  contact_info: {
    phone: string;
    email: string;
    emergency_contact: string;
  };
  is_active: boolean;
  is_favorite?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  
  // Collaborators field
  collaborators?: Array<{
    user_id: string;
    user_name: string;
    role: string;
    manager_assignments?: Array<{
      manager_type: string;
      permissions: string[];
    }>;
  }>;
  assigned_roles?: string[];
}

export interface FarmInsertRequest {
  data: Farm;
}

export interface FarmSearchRequest {
  bind_vars: {
    '@collection': string;
    [key: string]: any;
  };
  query: string;
}

export interface FarmAggregateRequest {
  bind_vars: {
    '@collection': string;
    '@collectionAccess': string;
    [key: string]: any;
  };
  query: string;
}

export interface FarmUpdateRequest {
  bind_vars: {
    '@collection': string;
    key: string;
    updates: Partial<Farm>;
    [key: string]: any;
  };
  query: string;
}

export interface FarmResponse {
  success: boolean;
  data?: any;
  message?: string;
  error?: string;
}

/**
 * Service for managing Farm operations
 * Provides methods for insert, search, and update operations
 */
export class FarmService {
  private readonly endpoint = '/Farms';

  constructor() {
    // Use the singleton apiClient instance
  }

  /**
   * Insert new Farm document(s)
   * POST /api/v2/Farm/insert
   * 
   * @param request - The insert request containing farm data to be added
   * @returns Promise with the response from the API
   */
  async insert(request: FarmInsertRequest): Promise<FarmResponse> {
    try {
      // Route via BFF to inject server credentials and avoid invalid JWT upstream
      const response = await apiClientBff.post(`/farms/insert`, request);
      
      return {
        success: true,
        data: response,
        message: 'Farm inserted successfully'
      };
    } catch (error: any) {
      console.error('Erro na API de fazenda:', error);
      return {
        success: false,
        error: error.message || 'Failed to insert Farm',
        message: 'Insert operation failed'
      };
    }
  }

  /**
   * Search Farm documents using AQL query
   * POST /api/v2/Farm/search
   * 
   * @param request - The search request with AQL query and bind variables
   * @returns Promise with the search results
   */
  async search(request: FarmSearchRequest): Promise<FarmResponse> {
    try {
      const response = await apiClientBff.post(`/farms/search`, request);
      
      return {
        success: true,
        data: Array.isArray(response) ? response : (response && typeof response === 'object' && 'data' in response ? (response as any).data : []) || [],
        message: 'Search completed successfully'
      };
    } catch (error: any) {
      console.error('Error searching Farm:', error);
      return {
        success: false,
        error: error.message || 'Failed to search Farm',
        message: 'Search operation failed'
      };
    }
  }

  async aggregate(request: FarmAggregateRequest): Promise<FarmResponse> {
    try {
      const response = await apiClientBff.post(`/farms/search`, request);
      const data = Array.isArray(response)
        ? response
        : (response && typeof response === 'object' && 'data' in response ? (response as any).data : []) || [];

      return {
        success: true,
        data,
        message: 'Aggregate completed successfully'
      };
    } catch (error: any) {
      console.error('Error aggregating Farm:', error);
      return {
        success: false,
        error: error.message || 'Failed to aggregate Farm',
        message: 'Aggregate operation failed'
      };
    }
  }

  async aggregateByUser(userId: string): Promise<FarmResponse> {
    const request: FarmAggregateRequest = {
      bind_vars: {
        '@collection': 'Farms',
        '@collectionAccess': 'FarmAccessControl',
        uid: userId
      },
      query: `
        FOR doc IN @@collection
          LIMIT 1
          LET owner_farms = (
            FOR f IN @@collection
              FILTER f.owner_id == @uid
              RETURN f
          )
          LET assigned_farms = (
            FOR fac IN @@collectionAccess
              FILTER @uid IN fac.assignments[*].user_id
              LET f = DOCUMENT("Farms", fac.farm_id)
              FILTER f != null
              LET myRoles = fac.assignments[* FILTER CURRENT.user_id == @uid].role_id
              RETURN { farm: f, roles: myRoles }
          )
          RETURN { owner_farms: owner_farms, assigned_farms: assigned_farms }
      `
    };

    return this.aggregate(request);
  }

  /**
   * Update Farm documents using AQL query
   * POST /api/v2/Farm/update
   * 
   * @param request - The update request with AQL query and bind variables
   * @returns Promise with the update results
   */
  async update(request: FarmUpdateRequest): Promise<FarmResponse> {
    try {
      const response = await apiClientBff.post(`/farms/update`, request);
      
      return {
        success: true,
        data: response,
        message: 'Farm updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating Farm:', error);
      return {
        success: false,
        error: error.message || 'Failed to update Farm',
        message: 'Update operation failed'
      };
    }
  }

  /**
   * Helper method to search by owner ID
   * 
   * @param ownerId - The owner ID to search for
   * @returns Promise with the search results
   */
  async searchByOwner(ownerId: string): Promise<FarmResponse> {
    const searchRequest: FarmSearchRequest = {
      bind_vars: {
        '@collection': 'Farms',
        owner_id: ownerId
      },
      query: 'FOR doc IN @@collection FILTER doc.owner_id == @owner_id RETURN doc'
    };

    return this.search(searchRequest);
  }

  /**
   * Helper method to search by name
   * 
   * @param name - The farm name to search for
   * @returns Promise with the search results
   */
  // Fix collection name from 'Farm' to 'Farms'
  async searchByName(name: string, userId?: string): Promise<FarmResponse> {
    const bind_vars: any = {
      '@collection': 'Farms', // Fixed: was 'Farm'
      name: name
    };

    let filterClause = 'FILTER CONTAINS(LOWER(doc.name), LOWER(@name))';
    
    if (userId) {
      bind_vars.userId = userId;
      filterClause += ' AND (doc.owner_id == @userId OR @userId IN doc.collaborators[*].user_id)';
    }

    const searchRequest: FarmSearchRequest = {
      bind_vars,
      query: `FOR doc IN @@collection ${filterClause} RETURN doc`
    };
    return this.search(searchRequest);
  }

  /**
   * Search farms where user is a collaborator
   */
  async searchByCollaborator(userId: string): Promise<FarmResponse> {

    
    const request: FarmSearchRequest = {
      bind_vars: {
        '@collection': 'Farms',
        userId: userId
      },
      query: `
        FOR doc IN @@collection 
        FILTER LENGTH(doc.collaborators[* FILTER CURRENT.user_id == @userId]) > 0
        RETURN doc
      `
    };
    
    const response = await this.search(request);

    
    return response;
  }

  /**
   * Helper method to search by farm type
   * 
   * @param farmType - The farm type to search for
   * @returns Promise with the search results
   */
  async searchByFarmType(farmType: string): Promise<FarmResponse> {
    const searchRequest: FarmSearchRequest = {
      bind_vars: {
        '@collection': 'Farms',
        farm_type: farmType
      },
      query: 'FOR doc IN @@collection FILTER doc.farm_type == @farm_type RETURN doc'
    };

    return this.search(searchRequest);
  }

  /**
   * Helper method to update by key
   * 
   * @param key - The document key to update
   * @param updates - The fields to update
   * @returns Promise with the update results
   */
  async updateByKey(key: string, updates: Partial<Farm>): Promise<FarmResponse> {
    const updateRequest: FarmUpdateRequest = {
      bind_vars: {
        '@collection': 'Farms',
        key: key,
        updates: {
          ...updates,
          updated_at: new Date().toISOString()
        }
      },
      query: 'FOR doc IN @@collection FILTER doc._key == @key UPDATE doc WITH @updates IN @@collection RETURN NEW'
    };

    return this.update(updateRequest);
  }

  /**
   * Helper method to toggle favorite status of a farm
   * 
   * @param farmId - The farm ID or key to update
   * @param ownerId - The owner ID for security validation
   * @param isFavorite - The new favorite status
   * @returns Promise with the update results
   */
  async toggleFavorite(farmId: string, ownerId: string, isFavorite: boolean): Promise<FarmResponse> {
    // Alternando status de favorito
    
    const updateRequest: FarmUpdateRequest = {
      bind_vars: {
        '@collection': 'Farms',
        key: farmId,
        owner_id: ownerId,
        updates: {
          is_favorite: isFavorite,
          updated_at: new Date().toISOString()
        }
      },
      query: 'FOR doc IN @@collection FILTER doc._key == @key AND doc.owner_id == @owner_id UPDATE doc WITH @updates IN @@collection RETURN NEW'
    };

    // Atualizando fazenda
    
    const result = await this.update(updateRequest);
    
    // Resultado da atualização
    
    return result;
  }

  /**
   * Helper method to create a farm with all required fields
   * 
   * @param farmData - The farm data to insert
   * @returns Promise with the insert results
   */
  async createFarm(farmData: Omit<Farm, 'id' | 'created_at' | 'updated_at'>): Promise<FarmResponse> {
    // Iniciando criação de fazenda
    
    // Normalizar farm_type para sempre ser um array conforme schema
    const normalizedFarmType = Array.isArray(farmData.farm_type)
      ? farmData.farm_type
      : [farmData.farm_type || 'other'];

    const farm: Farm = {
      ...farmData,
      farm_type: normalizedFarmType,
      is_active: farmData.is_active ?? true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      // Defaults for audit fields
      ...(typeof (farmData as any).created_by === 'undefined' ? { created_by: (farmData as any).owner_id } : {}),
      ...(typeof (farmData as any).updated_by === 'undefined' ? { updated_by: (farmData as any).owner_id } : {})
    };
    
    // Dados com timestamps adicionados

    const insertRequest: FarmInsertRequest = {
      data: farm
    };
    
    // Chamando serviço de inserção

    const result = await this.insert(insertRequest);
    
    // Resultado do insert processado

    return result;
  }

  /**
   * Check if a farm with the given name already exists for the owner
   * @param name - Farm name to check
   * @param ownerId - Owner ID to scope the search
   * @returns Promise with validation result
   */
  async checkFarmNameExists(name: string, ownerId: string): Promise<FarmResponse> {
    const searchRequest: FarmSearchRequest = {
      bind_vars: {
        '@collection': 'Farms',
        name: name.trim(),
        owner_id: ownerId
      },
      query: 'FOR doc IN @@collection FILTER doc.owner_id == @owner_id AND LOWER(doc.name) == LOWER(@name) RETURN doc'
    };
    return this.search(searchRequest);
  }

  /**
   * Check if a farm with the given registration number already exists
   * @param registrationNumber - Registration number to check
   * @returns Promise with validation result
   */
  async checkRegistrationExists(registrationNumber: string): Promise<FarmResponse> {
    if (!registrationNumber?.trim()) {
      return { success: true, data: { data: [] } }; // No registration number to check
    }
    
    const searchRequest: FarmSearchRequest = {
      bind_vars: {
        '@collection': 'Farms',
        registration_number: registrationNumber.trim()
      },
      query: 'FOR doc IN @@collection FILTER doc.registration_number == @registration_number RETURN doc'
    };
    return this.search(searchRequest);
  }

  /**
   * Comprehensive validation before farm creation
   * @param farmData - Farm data to validate
   * @returns Promise with validation result and details
   */
  async validateFarmForCreation(farmData: Omit<Farm, 'id' | 'created_at' | 'updated_at'>): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  }> {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check for duplicate name within owner's farms
    const nameCheck = await this.checkFarmNameExists(farmData.name, farmData.owner_id);
    if (nameCheck.success && nameCheck.data?.data?.length > 0) {
      errors.push(`A farm named "${farmData.name}" already exists for this owner`);
    }

    // Check for duplicate registration number globally
    if (farmData.registration_number) {
      const regCheck = await this.checkRegistrationExists(farmData.registration_number);
      if (regCheck.success && regCheck.data?.data?.length > 0) {
        errors.push(`Registration number "${farmData.registration_number}" is already in use`);
      }
    }

    // Additional validations
    if (!farmData.name?.trim()) {
      errors.push('Farm name is required');
    }
    
    if (farmData.name && farmData.name.length < 2) {
      errors.push('Farm name must be at least 2 characters long');
    }

    if (farmData.area_hectares && farmData.area_hectares < 0) {
      errors.push('Farm area cannot be negative');
    }

    if (farmData.capacity_head && farmData.capacity_head < 0) {
      errors.push('Animal capacity cannot be negative');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
}

// Export singleton instance
export const farmService = new FarmService();
